import { type Plugin, shallowRef, triggerRef } from 'vue';
import { GrowthBook } from '@growthbook/growthbook';
import { useAuthStore } from '@tundr/auth';
import {
  FEATURE_FLAG_TOKEN,
  setGrowthBookRef,
  setGrowthBookInitPromise,
} from './config.ts';
import type { FeatureFlagPluginOptions } from './types.ts';

export const FeatureFlagPlugin: Plugin<FeatureFlagPluginOptions> = (
  app,
  {
    apiHost,
    clientKey,
    attributes,
    enableDevMode = false,
    streaming = true,
    syncAttributesFromAuthStore = false,
    trackingCallback,
  },
) => {
  const trimmedClientKey = clientKey?.trim() ?? '';
  const hasClientKey = trimmedClientKey.length > 0;

  const gb = new GrowthBook({
    apiHost,
    clientKey: hasClientKey ? trimmedClientKey : '',
    attributes,
    enableDevMode,
    trackingCallback,
  });

  const gbRef = shallowRef(gb);

  gb.setRenderer(() => {
    triggerRef(gbRef);
  });

  const initPromise = hasClientKey
    ? gb.init({ streaming }).catch((err) => {
        console.warn(
          '[FeatureFlagPlugin] GrowthBook init failed, using defaults:',
          err,
        );
      })
    : Promise.resolve();

  setGrowthBookInitPromise(initPromise);

  if (syncAttributesFromAuthStore) {
    const authStore = useAuthStore();

    authStore.$subscribe(
      (_, state) => {
        if (state.authData?.user) {
          gb.setAttributes({
            id: state.authData.user.id,
            companyId: state.selectedCompany?.id ?? '',
          });
        } else {
          gb.setAttributes({});
        }
      },
      { detached: true, immediate: true },
    );
  }

  setGrowthBookRef(gbRef);
  app.provide(FEATURE_FLAG_TOKEN, gbRef);
};
